import React from 'react';
import styled from 'styled-components';

function SecurityCenter({ onClose }) {
  return (
    <Div>
      {/* Yellow warning banner */}
      <div className="sc__alert">
        <div className="sc__alert__icon">
          <svg viewBox="0 0 24 24" width="32" height="32">
            <path d="M12 2L1 21h22L12 2z" fill="#cc9900" stroke="#996600" strokeWidth="1" />
            <text x="12" y="18" textAnchor="middle" fill="white" fontWeight="bold" fontSize="14">!</text>
          </svg>
        </div>
        <div className="sc__alert__text">
          <div className="sc__alert__title">Your computer might be at risk</div>
          <div className="sc__alert__sub">Antivirus software might not be installed or is out of date. Click <span className="sc__link">Recommendations</span> for suggested actions you can take to protect your computer.</div>
        </div>
      </div>
      {/* Main two-column layout */}
      <div className="sc__body">
        {/* Left sidebar - Resources */}
        <div className="sc__sidebar">
          <div className="sc__sidebar__section">
            <div className="sc__sidebar__title">Resources</div>
            <div className="sc__sidebar__link">Check for the latest updates from Windows Update</div>
            <div className="sc__sidebar__link">Check for the latest virus and spyware definitions</div>
            <div className="sc__sidebar__link">Change the way Security Center alerts me</div>
          </div>
          <div className="sc__sidebar__section sc__sidebar__section--cta">
            <div className="sc__sidebar__title">Need IT Support?</div>
            <div className="sc__sidebar__link sc__sidebar__link--bold">Dead Simple Computing</div>
            <div className="sc__sidebar__link sc__sidebar__link--phone">0118 359 2220</div>
            <div className="sc__sidebar__link">deadsimplecomputing.co.uk</div>
          </div>
        </div>
        {/* Main content - status items */}
        <div className="sc__main">
          <div className="sc__heading">Security essentials</div>
          <div className="sc__subhead">Security Center checks whether your computer has the essentials for security.</div>

          {/* Firewall */}
          <div className="sc__status sc__status--red">
            <div className="sc__status__header">
              <div className="sc__status__arrow">▼</div>
              <div className="sc__status__label">Firewall</div>
              <div className="sc__status__badge sc__badge--off">OFF</div>
            </div>
            <div className="sc__status__detail">
              Windows Firewall is <strong>turned off</strong>. Your computer is not protected from unauthorised access through the Internet or a network. <span className="sc__link">Recommendations...</span>
            </div>
          </div>

          {/* Automatic Updates */}
          <div className="sc__status sc__status--red">
            <div className="sc__status__header">
              <div className="sc__status__arrow">▼</div>
              <div className="sc__status__label">Automatic Updates</div>
              <div className="sc__status__badge sc__badge--off">OFF</div>
            </div>
            <div className="sc__status__detail">
              Automatic Updates is turned off. Your PC is <strong>not set to automatically download and install updates</strong>. Last checked: 14 March 2022. <span className="sc__link">Turn on Automatic Updates</span>
            </div>
          </div>

          {/* Virus Protection */}
          <div className="sc__status sc__status--red">
            <div className="sc__status__header">
              <div className="sc__status__arrow">▼</div>
              <div className="sc__status__label">Virus Protection</div>
              <div className="sc__status__badge sc__badge--notfound">NOT FOUND</div>
            </div>
            <div className="sc__status__detail">
              <strong>No antivirus program was detected on this computer.</strong> Norton AntiVirus subscription expired 14/03/2022. Your computer is not protected against viruses and other security threats. <span className="sc__link">Recommendations...</span>
            </div>
          </div>

          {/* Divider & Managed Services */}
          <div className="sc__divider" />
          <div className="sc__heading">Manage security settings for:</div>

          {/* Additional items */}
          <div className="sc__status sc__status--amber">
            <div className="sc__status__header">
              <div className="sc__status__arrow">▶</div>
              <div className="sc__status__label">Internet Options</div>
            </div>
          </div>
          <div className="sc__status sc__status--amber">
            <div className="sc__status__header">
              <div className="sc__status__arrow">▶</div>
              <div className="sc__status__label">Windows Update</div>
            </div>
          </div>

          {/* DSC CTA */}
          <div className="sc__divider" />
          <div className="sc__cta">
            <div className="sc__cta__shield">✕</div>
            <div className="sc__cta__content">
              <div className="sc__cta__title">This is not okay.</div>
              <div className="sc__cta__text">Your business is exposed to ransomware, phishing, data breaches, and regulatory fines. Everything on this page is OFF or expired.</div>
              <div className="sc__cta__text"><strong>Dead Simple Computing</strong> provides managed IT security, Microsoft 365, and Cyber Essentials certification.</div>
              <div className="sc__cta__phone">Call: 0118 359 2220</div>
              <div className="sc__cta__web">deadsimplecomputing.co.uk</div>
            </div>
          </div>
        </div>
      </div>
    </Div>
  );
}

const Div = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  background: #ece9d8;
  font-family: Tahoma, 'Segoe UI', sans-serif;
  font-size: 11px;
  color: #000;

  /* Yellow warning banner at top */
  .sc__alert {
    background: linear-gradient(to bottom, #fffde0, #fff3b0);
    border: 1px solid #d4c462;
    border-radius: 3px;
    margin: 8px 12px 4px;
    padding: 10px 14px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }
  .sc__alert__icon {
    flex-shrink: 0;
  }
  .sc__alert__title {
    font-size: 14px;
    font-weight: bold;
    color: #8b6914;
    margin-bottom: 3px;
  }
  .sc__alert__sub {
    font-size: 11px;
    color: #333;
    line-height: 1.5;
  }

  /* Two-column layout */
  .sc__body {
    flex: 1;
    display: flex;
    margin: 4px 12px 12px;
    overflow: hidden;
  }

  /* Left sidebar */
  .sc__sidebar {
    width: 175px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .sc__sidebar__section {
    background: linear-gradient(to bottom, #b1c8e8, #7ba0d4);
    border-radius: 6px;
    padding: 10px;
  }
  .sc__sidebar__section--cta {
    background: linear-gradient(to bottom, #d46b6b, #b54040);
  }
  .sc__sidebar__title {
    color: #fff;
    font-weight: bold;
    font-size: 12px;
    margin-bottom: 8px;
    text-shadow: 0 1px 1px rgba(0,0,0,0.3);
  }
  .sc__sidebar__link {
    color: #e0e8ff;
    font-size: 10px;
    padding: 3px 0;
    cursor: pointer;
    line-height: 1.4;
    &:hover { color: #fff; text-decoration: underline; }
  }
  .sc__sidebar__link--bold {
    color: #ffdd44;
    font-weight: bold;
    font-size: 12px;
    margin-top: 4px;
  }
  .sc__sidebar__link--phone {
    color: #ffdd44;
    font-weight: bold;
    font-size: 14px;
  }

  /* Main content area */
  .sc__main {
    flex: 1;
    background: #fff;
    border: 1px solid #7f9db9;
    border-radius: 0 6px 6px 0;
    margin-left: 8px;
    padding: 12px 14px;
    overflow-y: auto;
  }
  .sc__heading {
    font-size: 13px;
    font-weight: bold;
    color: #003399;
    margin-bottom: 4px;
  }
  .sc__subhead {
    font-size: 11px;
    color: #444;
    margin-bottom: 12px;
    line-height: 1.5;
  }

  /* Status items - XP green/red/amber bars */
  .sc__status {
    margin-bottom: 4px;
    border-radius: 3px;
    overflow: hidden;
  }
  .sc__status__header {
    display: flex;
    align-items: center;
    padding: 6px 10px;
    cursor: pointer;
    gap: 8px;
  }
  .sc__status--red .sc__status__header {
    background: linear-gradient(to bottom, #f0c0c0, #e09090);
    border: 1px solid #cc6666;
  }
  .sc__status--green .sc__status__header {
    background: linear-gradient(to bottom, #c0f0c0, #90e090);
    border: 1px solid #66cc66;
  }
  .sc__status--amber .sc__status__header {
    background: linear-gradient(to bottom, #f0e0c0, #e0d090);
    border: 1px solid #ccaa66;
  }
  .sc__status__arrow {
    font-size: 10px;
    color: #333;
    width: 12px;
    flex-shrink: 0;
  }
  .sc__status__label {
    font-weight: bold;
    font-size: 12px;
    flex: 1;
  }
  .sc__status__badge {
    font-size: 10px;
    font-weight: bold;
    padding: 1px 6px;
    border-radius: 2px;
  }
  .sc__badge--off {
    background: #cc0000;
    color: #fff;
  }
  .sc__badge--notfound {
    background: #cc0000;
    color: #fff;
  }
  .sc__badge--on {
    background: #00aa00;
    color: #fff;
  }
  .sc__status__detail {
    padding: 8px 10px 10px 30px;
    background: #fff;
    border: 1px solid #ddd;
    border-top: none;
    font-size: 11px;
    line-height: 1.5;
    color: #333;
  }

  .sc__link {
    color: #0066cc;
    cursor: pointer;
    &:hover { text-decoration: underline; }
  }

  .sc__divider {
    height: 1px;
    background: #c0c0c0;
    margin: 12px 0;
  }

  /* CTA box */
  .sc__cta {
    display: flex;
    gap: 12px;
    background: linear-gradient(to bottom, #fff0f0, #ffe0e0);
    border: 1px solid #cc6666;
    border-radius: 4px;
    padding: 14px;
    margin-top: 8px;
  }
  .sc__cta__shield {
    width: 40px;
    height: 40px;
    background: #cc0000;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 22px;
    font-weight: bold;
    flex-shrink: 0;
  }
  .sc__cta__title {
    font-size: 14px;
    font-weight: bold;
    color: #cc0000;
    margin-bottom: 4px;
  }
  .sc__cta__text {
    font-size: 11px;
    color: #333;
    line-height: 1.5;
    margin-bottom: 4px;
  }
  .sc__cta__phone {
    font-size: 14px;
    font-weight: bold;
    color: #003399;
    margin-top: 4px;
  }
  .sc__cta__web {
    font-size: 11px;
    color: #0066cc;
  }
`;

export default SecurityCenter;
